/*
 * The single source of truth for public URLs.
 *
 * The first path segment decides the page language (/محامي/… is Arabic,
 * /Avocat/… French, /Lawyer/… English), so every public page has one URL per
 * language and those URLs are linked with hreflang. Builders return decoded
 * paths (raw Arabic); use toHref()/absoluteUrl() for markup.
 *
 * Used by the API's page renderer and sitemaps, and by the UI through a
 * webpack alias. Plain CommonJS, no dependencies.
 */
const { GOVERNORATES, BAR_LEVELS } = require("./geo");
const { CATEGORIES, CATEGORY_SEGMENT } = require("./categories");

const LANGS = ["ar", "fr", "en"];
const DEFAULT_LANG = "ar";

// First path segment per page type and language. These are the URLs the site
// has always used, so existing links and indexed pages keep working.
const SECTIONS = {
  lawyers: { ar: "محامون", fr: "Avocats", en: "Lawyers" },
  lawyer: { ar: "محامي", fr: "Avocat", en: "Lawyer" },
  questions: { ar: "استشارات-قانونية", fr: "Questions-Reponses", en: "Legal-Advice" },
  blogs: { ar: "المجلة-القانونية", fr: "Magazine-Juridique", en: "Legal-Magazine" },
  about: { ar: "من-نحن", fr: "Qui-Somme-Nous", en: "About-Us" },
  contact: { ar: "اتصل-بنا", fr: "Contactez-Nous", en: "Contact-Us" },
  terms: { ar: "الخصوصية-و-الأمن", fr: "Confidentialite", en: "Privacy" },
};
// Questions and articles share their listing segment with their detail pages.
SECTIONS.question = SECTIONS.questions;
SECTIONS.blog = SECTIONS.blogs;

const HOME = { ar: "/", fr: "/fr", en: "/en" };

// Pages that must never be indexed. Matched case-insensitively on the first
// segment.
const PRIVATE_SECTIONS = [
  "login", "register", "user-dashboard", "lawyer-dashboard", "book-appointment",
  "thank-you", "verify", "reset-password", "landing-page", "operation-center",
];

const isLang = (lang) => LANGS.includes(lang);
const langOr = (lang, fallback = DEFAULT_LANG) => (isLang(lang) ? lang : fallback);

const ARABIC_DIACRITICS = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g;

/**
 * URL slug that keeps Arabic letters: "Maître Ben Ali (Sfax)" -> "maitre-ben-ali-sfax",
 * "أسامة  بن علي" -> "أسامة-بن-علي".
 */
function slugify(text, maxLength = 80) {
  const slug = String(text == null ? "" : text)
    .normalize("NFKD")
    .replace(/[\u0300-\u036F]/g, "")
    // Recompose so hamza forms (أ إ آ) survive; only vowel marks are dropped.
    .normalize("NFC")
    .replace(ARABIC_DIACRITICS, "")
    .toLowerCase()
    .replace(/[^a-z0-9\u0621-\u064A\u0660-\u0669\u06A9\u06AF\u06CC]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length <= maxLength) return slug;
  return slug.slice(0, maxLength).replace(/-[^-]*$/, "") || slug.slice(0, maxLength);
}

/** Loose comparison key: accents, alif/taa-marbuta variants, articles and spacing removed. */
function matchKey(text) {
  return String(text == null ? "" : text)
    .normalize("NFKD")
    .replace(/[\u0300-\u036F]/g, "")
    .normalize("NFC")
    .replace(ARABIC_DIACRITICS, "")
    .toLowerCase()
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/^(le|la|el|al)[\s-]+/, "")
    .replace(/[\s\-_'’]+/g, "");
}

const GOV_INDEX = new Map();
GOVERNORATES.forEach((gov) => {
  [gov.key, gov.ar, gov.fr, gov.en, slugify(gov.ar), ...gov.aliases].forEach((name) => {
    GOV_INDEX.set(matchKey(name), gov);
  });
});

/** Governorate for any known spelling (Arabic, French, English, URL segment), or null. */
function findGovernorate(value) {
  if (!value) return null;
  return GOV_INDEX.get(matchKey(value)) || null;
}

function governorateSegment(gov, lang) {
  return langOr(lang) === "ar" ? slugify(gov.ar) : gov.key;
}

function governorateName(gov, lang) {
  return gov[langOr(lang)];
}

/** Bar level for its number or URL segment in any language, or null. */
function findBarLevel(value) {
  if (value === null || value === undefined || value === "") return null;
  const asNumber = Number(value);
  return (
    BAR_LEVELS.find((level) => level.key === asNumber)
    || BAR_LEVELS.find((level) => LANGS.some((l) => matchKey(level.slug[l]) === matchKey(value)))
    || null
  );
}

const withPage = (path, page) => (page && Number(page) > 1 ? `${path}?page=${Number(page)}` : path);

function homePath(lang) {
  return HOME[langOr(lang)];
}

function sectionPath(type, lang) {
  return `/${SECTIONS[type][langOr(lang)]}`;
}

/** Lawyer name to show in a language: ONAT only publishes Arabic names. */
function lawyerName(lawyer, lang) {
  const ar = lawyer.nameInArabic;
  const fr = lawyer.nameInFrench;
  const frIsLatin = fr && /[a-z]/i.test(fr);
  if (langOr(lang) === "ar") return ar || fr || "";
  return (frIsLatin ? fr : ar) || fr || "";
}

function lawyerPath(lawyer, lang) {
  const slug = slugify(lawyerName(lawyer, lang)) || "profile";
  return `/${SECTIONS.lawyer[langOr(lang)]}/${slug}/${lawyer.id}`;
}

/** City / city x bar-level listing: /محامون/صفاقس/تعقيب, /Avocats/sfax/cassation?page=2 */
function lawyersPath(lang, { governorate, barLevel, page } = {}) {
  const l = langOr(lang);
  const gov = governorate && (governorate.key ? governorate : findGovernorate(governorate));
  const level = barLevel && (barLevel.slug ? barLevel : findBarLevel(barLevel));
  let path = sectionPath("lawyers", l);
  if (gov) path += `/${governorateSegment(gov, l)}`;
  if (gov && level) path += `/${level.slug[l]}`;
  return withPage(path, page);
}

/** A question lives in its own language only. */
function questionLang(question, fallback) {
  return isLang(question.language) ? question.language : langOr(fallback);
}

function questionPath(question, lang) {
  const l = langOr(lang || questionLang(question));
  return `/${SECTIONS.question[l]}/${slugify(question.title) || "question"}/${question.id}`;
}

function blogLang(blog, fallback) {
  return isLang(blog.language) ? blog.language : langOr(fallback);
}

function blogPath(blog, lang) {
  const l = langOr(lang || blogLang(blog));
  return `/${SECTIONS.blog[l]}/${slugify(blog.title) || "article"}/${blog.id}`;
}

const CATEGORY_INDEX = new Map();
CATEGORIES.forEach((category) => {
  [category.key, category.ar, category.fr, category.en, ...LANGS.map((l) => category.slug[l])].forEach((name) => {
    CATEGORY_INDEX.set(matchKey(name), category);
  });
});

/** Magazine topic for its key, a display name in any language or a URL slug; or null. */
function findCategory(value) {
  if (!value || typeof value !== "string") return null;
  return CATEGORY_INDEX.get(matchKey(value)) || null;
}

function categoryName(category, lang) {
  return category[langOr(lang)];
}

/** Every spelling a document may store for this topic (articles: display names; FAQ: the key). */
function categoryValues(category) {
  return [category.key, category.ar, category.fr, category.en];
}

/** Topic page: /المجلة-القانونية/تصنيف/قانون-الأسرة, /Magazine-Juridique/categorie/droit-de-la-famille */
function categoryPath(category, lang) {
  const l = langOr(lang);
  const cat = category && (category.slug ? category : findCategory(category));
  return `/${SECTIONS.blogs[l]}/${CATEGORY_SEGMENT[l]}/${cat.slug[l]}`;
}

// The magazine seeds one guide per topic and language as guide-<slug>-<lang>.
const GUIDE_ID = /^guide-(.+)-(ar|fr|en)$/;

/** { ar, fr, en } ids of the same guide in each language, or null for other articles. */
function guideSiblingIds(id) {
  const match = GUIDE_ID.exec(String(id || ""));
  if (!match) return null;
  return LANGS.reduce((acc, lang) => ({ ...acc, [lang]: `guide-${match[1]}-${lang}` }), {});
}

/**
 * Image to give social networks for an article cover. They do not render
 * SVG, so a site cover /images/blog/<name>.svg has a 1200x630 JPEG twin at
 * /images/blog/og/<name>.jpg (e2e/scripts/cover-images.js). Returns null
 * when there is no usable image (callers fall back to the site default).
 */
function ogImagePath(image) {
  const value = String(image || "");
  if (/^https?:\/\//i.test(value)) return value;
  const cover = /^\/images\/blog\/([\w-]+)\.svg$/.exec(value);
  if (cover) return `/images/blog/og/${cover[1]}.jpg`;
  if (/^\/[^/].*\.(png|jpe?g|webp)$/i.test(value)) return value;
  return null;
}

/** The hreflang x-default of a set of alternates: Arabic, else the first language present. */
function xDefault(alts) {
  if (!alts) return null;
  return alts[DEFAULT_LANG] || LANGS.map((l) => alts[l]).find(Boolean) || null;
}

/** { ar, fr, en } paths for a page that exists in every language. */
function alternates(build) {
  return LANGS.reduce((acc, lang) => ({ ...acc, [lang]: build(lang) }), {});
}

/** Percent-encode a decoded path for href/canonical use (idempotent on ASCII). */
function toHref(path) {
  return encodeURI(path);
}

function absoluteUrl(siteUrl, path) {
  return String(siteUrl).replace(/\/+$/, "") + toHref(path);
}

function safeDecode(segment) {
  try {
    return decodeURIComponent(segment);
  } catch (err) {
    return segment;
  }
}

// segment (lowercased) -> { type, lang }
const SECTION_INDEX = new Map();
Object.entries(SECTIONS).forEach(([type, byLang]) => {
  if (type === "question" || type === "blog") return;
  LANGS.forEach((lang) => SECTION_INDEX.set(byLang[lang].toLowerCase(), { type, lang }));
});
// Old spellings that have been linked or indexed.
SECTION_INDEX.set("privacy-policy", { type: "terms", lang: "en" });
SECTION_INDEX.set("qestions-reponses", { type: "questions", lang: "fr" });

const DETAIL_OF = { lawyers: "lawyer", questions: "question", blogs: "blog" };

/**
 * What a request path points to. Returns
 *   { type, lang, params }
 * type: home | lawyers | lawyer | questions | question | blogs | blog | category |
 *       about | contact | terms | private | username | unknown
 * The renderer compares the canonical path it builds with the request and
 * redirects when they differ.
 */
function parsePath(pathname, search = "") {
  const query = new URLSearchParams(search || "");
  const page = Math.max(1, parseInt(query.get("page"), 10) || 1);
  const segments = String(pathname || "/")
    .split("?")[0]
    .split("/")
    .filter(Boolean)
    .map(safeDecode);

  if (segments.length === 0) return { type: "home", lang: "ar", params: {} };
  const first = segments[0].toLowerCase();
  if (segments.length === 1 && (first === "fr" || first === "en")) {
    return { type: "home", lang: first, params: {} };
  }
  if (PRIVATE_SECTIONS.includes(first)) return { type: "private", lang: null, params: {} };

  const section = SECTION_INDEX.get(first);
  if (!section) {
    if (segments.length === 1) return { type: "username", lang: null, params: { username: segments[0] } };
    return { type: "unknown", lang: null, params: {} };
  }
  const { lang } = section;
  const rest = segments.slice(1);

  switch (section.type) {
    case "lawyers": {
      if (rest.length === 0) return { type: "lawyers", lang, params: { page } };
      const governorate = findGovernorate(rest[0]);
      if (!governorate || rest.length > 2) return { type: "unknown", lang, params: {} };
      if (rest.length === 1) return { type: "lawyers", lang, params: { governorate, page } };
      const barLevel = findBarLevel(rest[1]);
      if (!barLevel) return { type: "unknown", lang, params: {} };
      return { type: "lawyers", lang, params: { governorate, barLevel, page } };
    }
    case "blogs":
      // /<magazine>/<topic segment>/<topic slug>: a topic page. Any language's
      // segment is accepted here and corrected by the canonical redirect.
      if (rest.length === 2 && LANGS.some((l) => CATEGORY_SEGMENT[l].toLowerCase() === rest[0].toLowerCase())) {
        const category = findCategory(rest[1]);
        if (category) return { type: "category", lang, params: { category } };
      }
    // falls through: /<magazine>/<slug>/<id> is an article
    case "lawyer":
    case "questions": {
      const detail = DETAIL_OF[section.type] || section.type;
      if (section.type !== "lawyer" && rest.length === 0) return { type: section.type, lang, params: { page } };
      // /<section>/<slug>/<id> ; the slug is decorative and corrected by redirect.
      if (rest.length === 2) return { type: detail, lang, params: { slug: rest[0], id: rest[1] } };
      return { type: "unknown", lang, params: {} };
    }
    default:
      if (rest.length === 0) return { type: section.type, lang, params: {} };
      return { type: "unknown", lang, params: {} };
  }
}

module.exports = {
  LANGS,
  DEFAULT_LANG,
  SECTIONS,
  GOVERNORATES,
  BAR_LEVELS,
  CATEGORIES,
  CATEGORY_SEGMENT,
  slugify,
  matchKey,
  findGovernorate,
  governorateSegment,
  governorateName,
  findBarLevel,
  homePath,
  sectionPath,
  lawyerName,
  lawyerPath,
  lawyersPath,
  questionLang,
  questionPath,
  blogLang,
  blogPath,
  findCategory,
  categoryName,
  categoryValues,
  categoryPath,
  guideSiblingIds,
  ogImagePath,
  xDefault,
  alternates,
  toHref,
  absoluteUrl,
  parsePath,
};
